import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminPortalLayout from '@/modules/admin/components/AdminPortalLayout';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { useToast } from '@/hooks/useToast';
import { useEmailTemplates } from '@/hooks/queries';
import { captureError } from '@/utils/errorHandler';

export default function AdminEmailTemplatesPage() {
  const { authFetch } = useAuthContext();
  const navigate = useNavigate();
  const { showToast, toasts, hideToast } = useToast();
  
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Query hook
  const { data: templates = [], isLoading: loading } = useEmailTemplates();

  const loadPreview = async (templateId) => {
    try {
      setLoadingPreview(true);
      const response = await authFetch(`/api/admin/email-templates?template=${templateId}`);
      
      if (!response.ok) {
        throw new Error('Failed to load preview');
      }

      const data = await response.json();
      setPreviewHtml(data.html || '');
      setPreviewSubject(data.subject || '');
      setSelectedTemplate(templateId);
    } catch (error) {
      captureError(error, {
        action: 'loadPreview',
        component: 'AdminEmailTemplatesPage',
        extra: { templateId },
      });
      console.error('Error loading preview:', error);
      showToast('Failed to load preview', 'error');
    } finally {
      setLoadingPreview(false);
    }
  };

  const userFacingTemplates = templates.filter(t => t.category === 'User-facing');
  const adminFacingTemplates = templates.filter(t => t.category === 'Admin-facing');

  return (
    <AdminPortalLayout toasts={toasts} onCloseToast={hideToast}>
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            📧 Email Templates
          </h1>
          <p className="text-gray-600">
            Preview all email templates sent to users and admins
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-zapt-purple mb-4"></div>
              <p className="text-gray-600">Loading templates...</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Template List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Templates</h2>
                
                {/* User-facing Templates */}
                {userFacingTemplates.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">User-facing</h3>
                    <div className="space-y-2">
                      {userFacingTemplates.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => loadPreview(template.id)}
                          className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                            selectedTemplate === template.id
                              ? 'border-zapt-purple bg-zapt-purple/10'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="font-medium text-gray-900 text-sm">{template.name}</div>
                          <div className="text-xs text-gray-500 mt-1">{template.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Admin-facing Templates */}
                {adminFacingTemplates.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Admin-facing</h3>
                    <div className="space-y-2">
                      {adminFacingTemplates.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => loadPreview(template.id)}
                          className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                            selectedTemplate === template.id
                              ? 'border-zapt-purple bg-zapt-purple/10'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="font-medium text-gray-900 text-sm">{template.name}</div>
                          <div className="text-xs text-gray-500 mt-1">{template.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Preview */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                {selectedTemplate ? (
                  <>
                    <div className="mb-4 pb-4 border-b border-gray-200">
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        {templates.find(t => t.id === selectedTemplate)?.name}
                      </h2>
                      {previewSubject && (
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Subject:</span> {previewSubject}
                        </div>
                      )}
                    </div>

                    {loadingPreview ? (
                      <div className="flex items-center justify-center h-96">
                        <div className="text-center">
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-zapt-purple mb-2"></div>
                          <p className="text-gray-600 text-sm">Loading preview...</p>
                        </div>
                      </div>
                    ) : previewHtml ? (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <iframe
                          srcDoc={previewHtml}
                          className="w-full h-[800px] border-0"
                          title="Email Preview"
                          sandbox="allow-same-origin"
                        />
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <p>No preview available</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <p className="text-lg mb-2">Select a template to preview</p>
                    <p className="text-sm">Choose a template from the list on the left to see how it looks</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
    </AdminPortalLayout>
  );
}








